/**
 * CompanyPanel — S4 #company
 * canonical: lines 2055–2070
 * Saves: company_name, license_number, company_address, company_city, company_state, company_zip
 */
import { useState, useEffect } from 'react'
import { saveSettings, type CompanySettings } from '../../../api/settings'

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

interface Props {
  settings: CompanySettings | null
  onSaved: (s: CompanySettings) => void
}

export default function CompanyPanel({ settings, onSaved }: Props) {
  const [name,    setName]    = useState('')
  const [license, setLicense] = useState('')
  const [addr,    setAddr]    = useState('')
  const [city,    setCity]    = useState('')
  const [state,   setState_]  = useState('')
  const [zip,     setZip]     = useState('')
  const [status,  setStatus]  = useState<SaveStatus>('idle')
  const [errMsg,  setErrMsg]  = useState('')

  useEffect(() => {
    if (!settings) return
    setName(settings.company_name    ?? '')
    setLicense(settings.license_number ?? '')
    setAddr(settings.company_address   ?? '')
    setCity(settings.company_city      ?? '')
    setState_(settings.company_state   ?? '')
    setZip(settings.company_zip        ?? '')
  }, [settings])

  async function handleSave() {
    setStatus('saving')
    try {
      const updated = await saveSettings({
        company_name: name || null,
        license_number: license || null,
        company_address: addr || null,
        company_city: city || null,
        company_state: state || null,
        company_zip: zip || null,
      })
      onSaved(updated)
      setStatus('saved')
      setTimeout(() => setStatus('idle'), 2500)
    } catch (e) {
      setErrMsg((e as Error).message || 'Save failed')
      setStatus('error')
      setTimeout(() => setStatus('idle'), 4000)
    }
  }

  // Button label never shows raw API error — error appears below as separate text
  const saveLabel = status === 'saving' ? 'Saving…' : status === 'saved' ? 'Saved ✓' : 'Save profile'

  return (
    <div id="company" className="panel" data-testid="panel-company">
      <div className="panel-h">
        <div className="panel-tt">Company profile</div>
        <span className="panel-st">Used on pay apps, Pre Liens, and shown to subs</span>
      </div>
      <div className="panel-bp">
        <div className="fld-grid">
          <div className="fld">
            <label className="fld-l">Company name *</label>
            <input className="fld-i" value={name} onChange={e => setName(e.target.value)}
              placeholder="Sentio Development" data-testid="company-name-input" />
          </div>
          <div className="fld">
            <label className="fld-l">Contractor license #</label>
            <input className="fld-i" value={license} onChange={e => setLicense(e.target.value)}
              placeholder="CSLB #123456" data-testid="company-license-input" />
          </div>
        </div>
        <div className="fld" style={{ marginBottom: 12 }}>
          <label className="fld-l">Street address</label>
          <input className="fld-i" style={{ width: '100%', boxSizing: 'border-box' }}
            value={addr} onChange={e => setAddr(e.target.value)}
            placeholder="123 Main Street" data-testid="company-address-input" />
        </div>
        <div className="fld-grid" style={{ gridTemplateColumns: '1fr 100px 110px' }}>
          <div className="fld">
            <label className="fld-l">City</label>
            <input className="fld-i" value={city} onChange={e => setCity(e.target.value)}
              placeholder="Los Angeles" data-testid="company-city-input" />
          </div>
          <div className="fld">
            <label className="fld-l">State</label>
            <input className="fld-i" value={state} onChange={e => setState_(e.target.value)}
              placeholder="CA" maxLength={2} data-testid="company-state-input" />
          </div>
          <div className="fld">
            <label className="fld-l">ZIP</label>
            <input className="fld-i" value={zip} onChange={e => setZip(e.target.value)}
              placeholder="90210" data-testid="company-zip-input" />
          </div>
        </div>
        <div style={{ marginTop: 14 }}>
          <button
            className={`btn ${status === 'saved' ? 'b-gh' : 'b-dk'}`}
            disabled={status === 'saving'}
            onClick={handleSave}
            data-testid="save-profile-btn"
          >
            {saveLabel}
          </button>
          {status === 'error' && (
            <p style={{ color: 'var(--red)', fontSize: 11, marginTop: 6, marginBottom: 0 }}
              data-testid="save-profile-error">
              {errMsg === 'No token' ? 'Session expired — please refresh the page.' : errMsg || 'Save failed. Try again.'}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
